import { Injectable } from '@angular/core';
import { HttpUtilService } from './http-util.service';
import { EnvironmentService } from './environment.service';
import { BehaviorSubject, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { NotificationsService } from './notifications.service';
import { CognitoDomainService } from './cognito-domain.service';
import { TranslationService } from './translation.service';

@Injectable({
  providedIn: 'root',
})
export class IdentityProviderService {
  private _identityProvidersState: any = new BehaviorSubject({
    isLoading: true,
    isProcessing: false,
    identityProviders: [] as any[],
  });
  private hasCalledForIdentityProviders = false;
  private _idpSloUrl = new BehaviorSubject({ isLoading: true, url: '' });
  private hasAttemptedIdpSloUrl: boolean = false;
  private identityProvidersState$ = this._identityProvidersState.asObservable();

  constructor(
    private cognitoDomainService: CognitoDomainService,
    private notifications: NotificationsService,
    private http: HttpUtilService,
    private environmentService: EnvironmentService,
    private translationService: TranslationService
  ) {}

  getIdentityProviderState$() {
    if (!this.hasCalledForIdentityProviders) {
      this.hasCalledForIdentityProviders = true;
      this.getIdentityProvidersNetworkRequest();
    }
    return this.identityProvidersState$;
  }

  getIdentityProvidersNetworkRequest() {
    this.http
      .request(
        'get',
        `${this.environmentService.environment.apiRoot}/identity-provider`
      )
      .pipe(catchError(error => of({ error })))
      .subscribe(response => {
        if (response?.identityProviders && !response?.error) {
          this._identityProvidersState.next({
            isProcessing: false,
            isLoading: false,
            identityProviders: response?.identityProviders?.length
              ? response?.identityProviders.sort((a, b) =>
                  a.ProviderName.localeCompare(b.ProviderName)
                )
              : [],
          });
        } else {
          this._identityProvidersState.next({
            isProcessing: false,
            isLoading: false,
            identityProviders: [],
          });
        }
      });
  }

  updateIdentityProvidersState(updates) {
    this._identityProvidersState.next({
      ...this._identityProvidersState.getValue(),
      ...updates,
    });
  }

  createIdentityProvider(body) {
    this.updateIdentityProvidersState({ isProcessing: true });
    return this.http
      .request(
        'post',
        `${this.environmentService.environment.apiRoot}/identity-provider`,
        body
      )
      .pipe(catchError(error => of(error)))
      .pipe(
        map(response => {
          this.updateIdentityProvidersState({ isProcessing: false });
          !response.identityProvider
            ? this.handleErrorIdentityProviderResponse(response, 'Creating')
            : this.handleSuccessCreatingIdentityProviderResponse(
                response,
                body
              );
          return response.identityProvider;
        })
      );
  }

  updateIdentityProvider(updates) {
    this.updateIdentityProvidersState({ isProcessing: true });

    return this.http
      .request(
        'put',
        `${this.environmentService.environment.apiRoot}/identity-provider`,
        updates
      )
      .pipe(catchError(error => of(error)))
      .pipe(
        tap(response => {
          this.updateIdentityProvidersState({ isProcessing: false });

          !response.identityProvider
            ? this.handleErrorIdentityProviderResponse(response, 'Updating')
            : this.handleSuccessUpdatingIdentityProviderResponse(
                response,
                updates
              );
        })
      );
  }

  handleErrorIdentityProviderResponse(response, type) {
    const additionalInfo = response.errorMessage || response.error;
    this.notifications.popNotification(
      '',
      typeof additionalInfo === 'string'
        ? `${this.translationService.getTranslatedContent(
            'error',
            'Error'
          )} ${type} ${this.translationService.getTranslatedContent(
            'identity_provider',
            'Identity Provider'
          )}: ` + additionalInfo
        : `Error ${type} Identity Provider`
    );
  }

  deleteIdentityProvider(providerName) {
    this.http
      .request(
        'DELETE',
        `${this.environmentService.environment.apiRoot}/identity-provider`,
        { providerName }
      )
      .pipe(catchError(error => of({ error })))
      .subscribe(response => {
        !response || response.error || response.errorMessage
          ? this.handleErrorIdentityProviderResponse(response, 'Deleting')
          : this.handleSuccessDeleteIdentityProviderResponse(providerName);
      });
  }

  handleSuccessDeleteIdentityProviderResponse(providerName) {
    this.cognitoDomainService.updateCognitoSsoUrl();
    const identityProviders = this._identityProvidersState
      .getValue()
      .identityProviders.filter(
        identityProvider => identityProvider.ProviderName !== providerName
      );
    this.updateIdentityProvidersState({ identityProviders });

    this.notifications.popNotification(
      '',
      this.translationService.getTranslatedContent(
        'succesfully_removed_identity_provider._it_may_take_around_5_minutes_until_you_are_able_to_see_sso_updates.',
        'Succesfully Removed Identity Provider. It may take around 5 minutes until you are able to see SSO updates.'
      )
    );
  }

  handleSuccessUpdatingIdentityProviderResponse(updates, body) {
    this.notifications.popNotification(
      '',
      this.translationService.getTranslatedContent(
        'succesfully_updated_identity_provider._it_may_take_around_5_minutes_until_you_are_able_to_see_sso_updates.',
        'Succesfully Updated Identity Provider. It may take around 5 minutes until you are able to see SSO updates.'
      )
    );

    this.cognitoDomainService.updateCognitoSsoUrl();

    if (updates.wasEnableUpdated === false) {
      this.notifications.popNotification(
        '',
        this.translationService.getTranslatedContent(
          'issue_enabling_identity_provider_at_this_time.',
          'Issue enabling Identity Provider at this time.'
        )
      );
    }

    let identityProviders = this._identityProvidersState
      .getValue()
      .identityProviders.map(identityProvider =>
        identityProvider.ProviderName === updates.identityProvider.ProviderName
          ? {
              ...updates.identityProvider,
              enabled:
                updates.wasEnableUpdated && body.hasOwnProperty('enabled')
                  ? body.enabled
                  : identityProvider.enabled,
            }
          : identityProvider
      );
    this.updateIdentityProvidersState({ identityProviders });
  }
  handleSuccessCreatingIdentityProviderResponse(response, body) {
    this.notifications.popNotification(
      '',
      this.translationService.getTranslatedContent(
        'succesfully_created_identity_provider._it_may_take_around_5_minutes_until_you_are_able_to_see_sso_updates.',
        'Succesfully Created Identity Provider. It may take around 5 minutes until you are able to see SSO updates.'
      )
    );

    this.cognitoDomainService.updateCognitoSsoUrl();

    let identityProviders: any =
      this._identityProvidersState.getValue().identityProviders;

    if (response.wasEnableUpdated === false) {
      this.notifications.popNotification(
        '',
        this.translationService.getTranslatedContent(
          'issue_enabling_identity_provider_at_this_time.',
          'Issue enabling Identity Provider at this time.'
        )
      );
    }

    identityProviders = [
      ...identityProviders,
      {
        ...response.identityProvider,
        enabled: response.wasEnableUpdated ? body?.enabled : false,
      },
    ].sort((a, b) => a.ProviderName.localeCompare(b.ProviderName));

    this.updateIdentityProvidersState({ identityProviders });
  }

  getSloUrl$() {
    if (!this.hasAttemptedIdpSloUrl) {
      this.hasAttemptedIdpSloUrl = true;
      this.getIdpSloUrlNetworkRequest();
    }
    return this._idpSloUrl.asObservable();
  }

  getIdpSloUrlNetworkRequest() {
    this.http
      .request(
        'GET',
        this.environmentService.environment.apiRoot +
          '/identity-provider?type=slo'
      )
      .pipe(catchError(error => of({ error })))
      .pipe(
        tap((response: any) => {
          this._idpSloUrl.next({
            isLoading: false,
            url: response?.url
              ? `${response.url}?post_logout_redirect_uri=${
                  encodeURIComponent('https://' + window.location.hostname) +
                  '/sso/logout'
                }`
              : '',
          });
        })
      )
      .subscribe();
  }
}
